/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
import {DestroyRef, Injectable} from '@angular/core';
import {AlertModalService} from '../../../../common/component/modal';
import {AppConfigService} from '../../../../common/service/app-config.service';
import {MapService} from '../../../services/map.service';
import {SpatialSequenceService} from '../../../../common/service/spatial-sequence.service';
import {FeatureApplyUpdateService} from '../../../services/feature-apply-update.service';
import {FeaturesApplyInsertService} from '../../../services/features-apply-insert.service';
import {FeatureApplyDeleteService} from '../../../services/feature-apply-delete.service';
import {AnnotationUtilityService} from '../../../services/features/annotation-utility.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {PrintAreaService} from '../../../../common/service/print-area.service';
import {EnlargementUtilService} from './enlargement-util.service';
import {EnlargementCreateService} from './enlargement-create.service';
import {NgEventBus} from 'ng-event-bus';
import {equals, isDefined} from '../../../../common/service/data-util';
import {EsriModuleService} from '../../../esri-nlr/services/esri-module.service';
import {switchMap, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

interface FeatureEvent {
    features: any;
    featureLayer: any;
}

@Injectable({
    providedIn: 'root'
})
export class EnlargementEventsService {
    constructor(
        private alertModalService: AlertModalService,
        private appConfigService: AppConfigService,
        private mapService: MapService,
        private spatialSequenceService: SpatialSequenceService,
        private enlargementCreateService: EnlargementCreateService,
        private featuresApplyUpdateService: FeatureApplyUpdateService,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private featuresApplyDeleteService: FeatureApplyDeleteService,
        private enlargementUtilService: EnlargementUtilService,
        private annotationUtilService: AnnotationUtilityService,
        private spatialUtilService: SpatialUtilService,
        private printAreaService: PrintAreaService,
        private eventBus: NgEventBus,
        private esriModuleService: EsriModuleService,
        private destroyRef: DestroyRef
    ) {
        // feature create
        this.eventBus
            .on(this.appConfigService.getDesignProperties().EVENTS.FEATURES_CREATED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                console.debug(this.title + ' : ' + this.appConfigService.getDesignProperties().EVENTS.FEATURES_CREATED + ' by watch');
                const {features, featureLayer} = meta.data as FeatureEvent;
                this.processFeatureEvent(features, featureLayer, this.appConfigService.getDesignProperties().EVENTS.FEATURES_CREATED);
            });

        // point feature update
        this.eventBus
            .on(this.appConfigService.getDesignProperties().EVENTS.POINT_FEATURES_UPDATED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                console.debug(this.title + ' : ' + this.appConfigService.getDesignProperties().EVENTS.POINT_FEATURES_UPDATED + ' by watch');
                const {features, featureLayer} = meta.data as FeatureEvent;
                this.processFeatureEvent(features, featureLayer, this.appConfigService.getDesignProperties().EVENTS.POINT_FEATURES_UPDATED);
            });

        // geometry feature update
        this.eventBus
            .on(this.appConfigService.getDesignProperties().EVENTS.GEOMETRY_FEATURES_UPDATED)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                console.debug(this.title + ' : ' + this.appConfigService.getDesignProperties().EVENTS.GEOMETRY_FEATURES_UPDATED + ' by watch');
                const {features, featureLayer} = meta.data as FeatureEvent;
                this.processFeatureEvent(features, featureLayer, this.appConfigService.getDesignProperties().EVENTS.GEOMETRY_FEATURES_UPDATED);
            });

        // enlargement name update
        this.eventBus
            .on('updateEnlargement')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                const {enlargement, originalName: originalName} = meta.data as {enlargement: any; originalName: any};
                console.debug(this.title + '  : updateEnlargement. ', enlargement, originalName);
                const enlargementAnnotationFeatures = spatialUtilService
                    .getAnnotationGraphics()
                    .filter(f => f.attributes.enlargementid === enlargement.attributes.id && f.attributes.featuretype === 'enlargement');

                const annotationsForUpdate = [];

                enlargementAnnotationFeatures.forEach(function (annotation) {
                    if (annotation.attributes.attribute1 === originalName) {
                        annotation.attributes.attribute1 = enlargement.attributes.name;
                        annotationsForUpdate.push(annotation);
                    }
                });

                if (annotationsForUpdate.length > 0) {
                    eventBus.cast('updateAnnotations', annotationsForUpdate);
                }
            });
    }

    title = 'EnlargementEventsService';

    isRelatedFeatureInEnlargement(enlargement, feature, annotations) {
        let isInEnlargement = false;

        annotations.forEach(annotation => {
            if (annotation.attributes.parentid === feature.attributes.id && annotation.attributes.enlargementid === enlargement.attributes.id) {
                isInEnlargement = true;
            }
        });

        return isInEnlargement;
    }

    processFeatureEvent(features: any[], featureLayer, event) {
        // only process events in the main plan
        if (!this.enlargementUtilService.isMapModeMainPlan()) {
            if (
                features.some(f => {
                    return f.attributes.featuretype === 'angle' || f.attributes.featuretype === 'annotation';
                })
            ) {
                return;
            }
        }

        // ignore if there are no features to process
        if (!features || features.length === 0) {
            return;
        }

        // only process angle and annotations
        if (
            !equals(featureLayer.id, this.spatialUtilService.getAngleMapLayer().id) &&
            !equals(featureLayer.id, this.spatialUtilService.getAnnotationMapLayer().id)
        ) {
            console.debug(this.title + ' : feature layer not supported - no processing required.');
            return;
        }

        this.spatialUtilService.getEnlargements().forEach(enlargement => {
            const enlargementAnnotationFeatures = this.spatialUtilService
                .getAnnotationGraphics()
                .filter(f => f.attributes.enlargementid === enlargement.attributes.id);

            // test if added annotation intersects or is contained within the given enlargement feature layer
            let featuresToBeCreated = [];
            const featuresToBeUpdatedAfterCreation = [];

            const guidMap = {};

            // create a copy of all features, setting parent id.
            features.forEach(feature => {
                if (isDefined(feature) && isDefined(feature.attributes)) {
                    if (
                        !this.isFeatureWithinEnlargement(enlargement, feature, false) &&
                        enlargement.attributes.id === feature.attributes.enlargementid &&
                        equals(this.appConfigService.getDesignProperties().EVENTS.GEOMETRY_FEATURES_UPDATED, event)
                    ) {
                        // the feature is not contained within or intersects with the enlargement, has already been added and is moved as part
                        // of a geometry feature update
                        this.getEnlargementFeaturesForDeletion(feature, featureLayer, enlargement, function (enlargementFeatures) {
                            if (enlargementFeatures && enlargementFeatures.length > 0) {
                                this.deleteFeatures(enlargementFeatures, [feature], enlargement, featureLayer);
                            }
                        });
                    } else if (
                        this.isFeatureWithinEnlargement(enlargement, feature, false) &&
                        !this.isFeatureAlreadyAddedToEnlargement(enlargement, feature)
                    ) {
                        // Create and set copies of angles or annotations that have been created on MainPlan for enlargements to featuresToBeCreated
                        // Store angles or annotations that have been created on MainPlan to featuresToBeUpdatedAfterCreation
                        if (
                            (equals(featureLayer.id, this.spatialUtilService.getAnnotationMapLayer().id) &&
                                !this.isRelatedFeatureInEnlargement(enlargement, feature, enlargementAnnotationFeatures)) ||
                            equals(featureLayer.id, this.spatialUtilService.getAngleMapLayer().id)
                        ) {
                            // the feature is contained within or intersects with the enlargement and has not already been added
                            featuresToBeUpdatedAfterCreation.push(feature);

                            let annotationSetId = null;
                            // create an new annotation set id, if the existing annotation is part of a set
                            if (feature.attributes.attribute10) {
                                if (!guidMap[feature.attributes.attribute10]) {
                                    guidMap[feature.attributes.attribute10] = this.annotationUtilService.newAnnotationSetGuid();
                                }
                                annotationSetId = guidMap[feature.attributes.attribute10];
                            }

                            featuresToBeCreated.push(this.copyFeature(enlargement, feature, annotationSetId));
                            const annotationSet = this.findAnnotationSets(feature, featureLayer);
                            // add annotation sets, if applicable
                            annotationSet.forEach(relatedFeature => {
                                // ensure that the feature is not the same feature and that it hasn't already been added
                                if (
                                    !featuresToBeCreated.some(f => relatedFeature.attributes.id === f.attributes.id) &&
                                    !this.isFeatureAlreadyAddedToEnlargement(enlargement, relatedFeature)
                                ) {
                                    featuresToBeUpdatedAfterCreation.push(relatedFeature);
                                    featuresToBeCreated.push(this.copyFeature(enlargement, relatedFeature, annotationSetId));
                                }
                            });
                        }
                    }
                }
            });

            if (featuresToBeCreated.length) {
                // Remove duplicates
                featuresToBeCreated = featuresToBeCreated.filter((f, i, a) => f === a.find(v => equals(v.attributes, f.attributes)));

                const featureIds = this.spatialSequenceService.getSequence(featuresToBeCreated.length, featureLayer);

                if (event === this.appConfigService.getDesignProperties().EVENTS.POINT_FEATURES_UPDATED) {
                    this.createPointFeatures(enlargement.attributes.id, featureIds, featureLayer, featuresToBeCreated);
                } else {
                    this.enlargementCreateService.createFeatures(
                        enlargement.attributes.id,
                        featureIds,
                        featureLayer,
                        featuresToBeCreated,
                        enlargement.attributes.scale,
                        this.calculateScale(enlargement)
                    );
                }

                // update enlargement ids for existing features
                this.enlargementUtilService.updateEnlargementId(enlargement.attributes.id, featuresToBeUpdatedAfterCreation);

                // apply updates
                this.featuresApplyUpdateService.updateFeaturesByType(featureLayer, featuresToBeUpdatedAfterCreation).subscribe();
            }
        });
    }

    findAnnotationSets(feature, featureLayer) {
        if (!feature.attributes.childid || !feature.attributes.childtype) {
            // no child details specified
            return [];
        }

        if (feature.attributes.attribute9 && feature.attributes.attribute10) {
            return this.mapService.findFeaturesByTypeAndAttributes(
                featureLayer,
                ['childtype', 'childid', 'attribute9', 'attribute10'],
                [feature.attributes.childtype, feature.attributes.childid, feature.attributes.attribute9, feature.attributes.attribute10]
            );
        } else {
            return this.mapService.findFeaturesByTypeAndAttributes(
                featureLayer,
                ['childtype', 'childid'],
                [feature.attributes.childtype, feature.attributes.childid]
            );
        }
    }

    /**
     * Returns a flag indicating ift he given feature has already been added to the enlargement. True is exists in the enlargement, false otherwise.
     */
    isFeatureAlreadyAddedToEnlargement(enlargement, feature) {
        return feature.attributes.enlargementid || this.enlargementUtilService.getEnlargementIds(feature).indexOf(enlargement.attributes.id) !== -1;
    }

    /**
     * Finds the related features for a feature in a given enlargement and adds them to the array for deletion.
     */
    getEnlargementFeaturesForDeletion(feature, featureLayer, enlargement, callback) {
        switch (featureLayer.id) {
            case this.spatialUtilService.getAngleMapLayer().id:
                callback(
                    this.spatialUtilService
                        .getAngles()
                        .filter(f => f.attributes.enlargementid === enlargement.attributes.id && f.attributes.parentid === feature.attributes.id)
                );
                break;
            case this.spatialUtilService.getAnnotationMapLayer().id:
                callback(
                    this.spatialUtilService
                        .getAnnotations()
                        .filter(f => f.attributes.enlargementid === enlargement.attributes.id && f.attributes.parentid === feature.attributes.id)
                );
                break;
            default:
                callback(null);
                break;
        }
    }

    /**
     * Copies the given features.
     */
    copyFeature(enlargement, feature, annotationSetId) {
        const newFeature = new this.esriModuleService.Graphic(feature.geometry);
        newFeature.geometry = window.structuredClone(feature.geometry);
        newFeature.attributes = window.structuredClone(feature.attributes);
        newFeature.attributes.parentid = feature.attributes.id;
        newFeature.attributes.attribute10 = annotationSetId;

        if (feature.attributes.hiddenonenlargements) {
            newFeature.attributes.hiddenonenlargements = feature.attributes.hiddenonenlargements.includes(`[${enlargement.attributes.id}]`)
                ? `[${enlargement.attributes.id}]`
                : null;
        }

        if (feature.attributes.visible) {
            newFeature.attributes.visible = null;
        }

        if (feature.attributes.enlargementids) {
            newFeature.attributes.enlargementids = null;
        }

        return newFeature;
    }

    /**
     * Creates the given features and updates the enlargement ids for the related features.
     */
    createFeatures(featuresToBeCreated, featuresToBeUpdated, enlargement, featureLayer, createFeatureFunction) {
        const featureIds = this.spatialSequenceService.getSequence(featuresToBeCreated.length, featureLayer);

        // create the feature/s
        createFeatureFunction(
            enlargement.attributes.id,
            featureIds,
            featureLayer,
            featuresToBeCreated,
            enlargement.attributes.scale,
            this.calculateScale(enlargement)
        );

        // update enlargement ids for existing features
        this.enlargementUtilService.updateEnlargementId(enlargement.attributes.id, featuresToBeUpdated);

        // apply updates
        this.featuresApplyUpdateService.updateFeaturesByType(featureLayer, featuresToBeUpdated).subscribe();
    }

    /**
     * Deletes then given features and cleans up the enlargement ids in the related feature.
     */
    deleteFeatures(featuresToBeDeleted, featuresToBeUpdated, enlargement, featureLayer) {
        const subs = this.featuresApplyDeleteService
            .commitFeaturesDeletion(featureLayer, featuresToBeDeleted, false)
            .pipe(
                tap(meta => {
                    const deletedFeatures = meta as any[];
                    if (deletedFeatures && deletedFeatures.length <= 0) {
                        console.debug('Error deleting enlargement features!');
                        this.alertModalService.createAlertMessage('Error occurred deleting enlargement features.');
                    }
                    console.debug('Enlargement features have been successfully deleted!');

                    // set enlargement ids
                    featuresToBeUpdated.forEach(feature => {
                        this.enlargementUtilService.removeEnlargementId(feature, enlargement.attributes.id);
                    });
                }),
                switchMap(() => this.featuresApplyUpdateService.updateFeaturesByType(featureLayer, featuresToBeUpdated))
            )
            .subscribe();
        this.eventBus.cast('nlr:busy', subs);
    }

    /**
     * Creates the spatial sequences for the given features.
     */
    createSpatialSequences(featureLayer, featuresToBeCreated, callback) {
        let featureLayerToGet = undefined;

        switch (featureLayer.id) {
            case this.spatialUtilService.getAngleMapLayer().id:
                featureLayerToGet = this.mapService.angleFeatureLayer;
                break;
            case this.spatialUtilService.getAnnotationMapLayer().id:
                featureLayerToGet = this.mapService.annotationFeatureLayer;
                break;
            default:
                break;
        }

        if (isDefined(featureLayerToGet)) {
            // create spatial sequence/s and execute callback
            const sequence = this.spatialSequenceService.getSequence(featuresToBeCreated.length, featureLayerToGet);
            if (callback) {
                callback(sequence);
            }
        }
    }

    /**
     * Calculates scale for the given enlargement.
     */
    calculateScale(enlargement) {
        return enlargement.attributes.scale / this.printAreaService.getReferenceScaleDivisor();
    }

    /**
     * Creates point features in the enlargement.
     */
    createPointFeatures(enlargementId, sequence, layer, featuresForCreation) {
        featuresForCreation.forEach((feature, index) => {
            feature.attributes['enlargementid'] = enlargementId;
            feature.attributes['id'] = sequence[index];
        });

        this.featuresApplyInsertService.insertFeaturesByType(layer, featuresForCreation, createdFeatures => {
            if (createdFeatures && createdFeatures.length > 0) {
                console.debug('Enlargement features have been successfully created!');
            } else {
                console.debug('Error creating enlargement features!');
                this.alertModalService.createAlertMessage('Error occurred creating enlargement features.');
            }
        });
    }

    /**
     * Returns a flag indicating if the feature is contained within or intersects with the enlargement. True if within enlargement, false otherwise.
     */
    isFeatureWithinEnlargement(enlargement, feature, intersects) {
        if (!feature.geometry) {
            return false;
        }

        // check if the enlargement contains the feature
        if (enlargement.geometry.contains(feature.geometry)) {
            return true;
        }

        // check if the feature intersects with the enlargement
        if (intersects && enlargement.geometry.getExtent().intersects(feature.geometry)) {
            return true;
        }

        // stop if the no related feature attributes
        if (!feature.attributes.featureid || !feature.attributes.featuretype) {
            return false;
        }

        const layerName = (feature.attributes.childtype ? feature.attributes.childtype : feature.attributes.featuretype) + 'FeatureLayer';
        const featureId = feature.attributes.childid ? feature.attributes.childid : feature.attributes.featureid;
        const layer = this.mapService[layerName];

        // no layer found
        if (!layer) {
            return false;
        }

        const layerFeature = this.mapService.findFeatureByTypeAndId(layer, featureId);

        // no feature found
        if (!layerFeature) {
            return false;
        }

        return this.isFeatureWithinEnlargement(enlargement, layerFeature, true);
    }
}
